"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  CoinsIcon,
  LogOutIcon,
  MoonIcon,
  RefreshCwIcon,
  SunIcon,
  UserIcon,
  UserRoundIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { SiteLoginLink } from "@/components/site/site-login-link";
import { useThemeToggle } from "@/hooks/use-theme-toggle";
import { apiFetch } from "@/lib/api-client";
import { logoutAuthSession } from "@/lib/auth/refresh-client";

const PROFILE_PATH = "/member/profile/";
const POINTS_PATH = "/member/points/";

type Member = {
  name: string;
  avatar: string;
  nickname: string;
  points: number;
};

const MENU_ITEM_BASE =
  "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground transition-colors";

const MENU_ITEM_CLASS = `${MENU_ITEM_BASE} hover:bg-muted`;

const MENU_ITEM_DANGER_CLASS = `${MENU_ITEM_BASE} hover:bg-destructive/10 hover:text-destructive`;

export function SiteUserMenu() {
  const t = useTranslations("site.common");
  const tt = useTranslations("site.theme");
  const router = useRouter();
  const { theme, toggle } = useThemeToggle();
  const [member, setMember] = useState<Member | null>(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;

    apiFetch<{ member?: Partial<Member> } | undefined>("/api/site/member-summary")
      .then((data) => {
        const raw = data?.member;

        if (!alive || !raw) {
          return;
        }

        setMember({
          name: raw.name ?? "",
          avatar: raw.avatar ?? "",
          nickname: raw.nickname ?? "",
          points: Number(raw.points) || 0,
        });
      })
      .catch(() => {});

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", onPointerDown);

    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  if (!member) {

    return (
      <SiteLoginLink className="flex size-9 items-center justify-center rounded-full border border-border bg-muted text-muted-foreground transition-colors hover:text-foreground">
        <UserIcon className="size-[18px]" aria-label={t("memberLogin")} />
      </SiteLoginLink>
    );
  }

  const displayName = member.nickname || member.name;
  const isDark = theme === "dark";

  async function handleLogout() {
    await logoutAuthSession("member");
    setMember(null);
    setOpen(false);
    toast.success(t("loggedOut"));

    router.refresh();
  }

  return (
    <div
      ref={wrapRef}
      className="relative flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={t("accountMenu")}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex size-9 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-muted-foreground"
      >
        {member.avatar ? (

          // eslint-disable-next-line @next/next/no-img-element
          <img
            className="size-full object-cover"
            src={member.avatar}
            alt={displayName}
          />
        ) : (
          <UserRoundIcon className="size-[18px]" />
        )}
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={t("accountMenu")}

          className="absolute top-full right-0 pt-2"
        >
          <div className="w-[185px] rounded-xl border border-border bg-popover p-1.5 shadow-lg">
            <div className="flex items-center gap-3 px-2.5 py-2">
              <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-muted-foreground">
                {member.avatar ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    className="size-full object-cover"
                    src={member.avatar}
                    alt={displayName}
                  />
                ) : (
                  <UserRoundIcon className="size-5" />
                )}
              </span>
              {/* 昵称与用户名都只占一行，长了截断成省略号——换行会把这一行撑高、
                  把下面的菜单顶下去 */}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">
                  {displayName}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {member.name}
                </span>
              </span>
            </div>

            <div className="my-1.5 h-px bg-border" role="presentation" />

            <Link
              href={PROFILE_PATH}
              role="menuitem"
              className={MENU_ITEM_CLASS}
              onClick={() => setOpen(false)}
            >
              <UserRoundIcon className="size-4" />
              {t("profile")}
            </Link>
            <Link
              href={POINTS_PATH}
              role="menuitem"
              className={MENU_ITEM_CLASS}
              onClick={() => setOpen(false)}
            >
              <CoinsIcon className="size-4 shrink-0" />
              <span className="shrink-0">{t("pointsManage")}</span>
              {/* 积分顶到右边；位数多了自己截断，不去挤左边的名字 */}
              <span className="ml-auto min-w-0 truncate text-xs tabular-nums text-muted-foreground">
                {member.points.toLocaleString("zh-CN")}
              </span>
            </Link>
            <button
              type="button"
              role="menuitem"
              className={MENU_ITEM_CLASS}
              onClick={() => {

                router.refresh();
                toast.success(t("cacheRefreshed"));
                setOpen(false);
              }}
            >
              <RefreshCwIcon className="size-4" />
              {t("refreshCache")}
            </button>
            <button
              type="button"
              role="menuitem"
              className={MENU_ITEM_CLASS}
              onClick={toggle}
            >
              {isDark ? (
                <SunIcon className="size-4" />
              ) : (
                <MoonIcon className="size-4" />
              )}
              {isDark ? tt("toLight") : tt("toDark")}
            </button>

            <div className="my-1.5 h-px bg-border" role="presentation" />

            <button
              type="button"
              role="menuitem"
              className={MENU_ITEM_DANGER_CLASS}
              onClick={handleLogout}
            >
              <LogOutIcon className="size-4" />
              {t("logout")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
